import { Pipe, PipeTransform } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { TravelerPaxResponse } from '@pax/entities/traveler-pax';
import { PaxFacade } from '@pax/pax.facade';
import { PaxDocumentsPipe } from '@pax/pipes/pax-documents.pipe';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { PaxType } from '@booking/entities/pax-type';
import { formatStrinWithTimeZoneDateByRegex } from '@ux/mappers/date.mapper';
import { travelerAgeInMonths, travelerAgeInYears } from '@pax/mappers/traveler-age.mapper';
import {
  GUEST_CHILDREN_RANGE_AGES, INFANTS_RANGE_AGES_IN_MONTH, PASSENGER_CHILDREN_RANGE_AGES
} from '@booking/constants/forms.constants';
import { TravelerTypes } from '@pax/entities/traveler-info';

@Pipe({
  name: 'travelerListVm',
  standalone: false
})
export class TravelerListVmPipe implements PipeTransform {

  constructor(
    private facade: PaxFacade,
    private paxDocumentsPipe: PaxDocumentsPipe,
  ) { }

  transform(
    travelersNotFormat: TravelerPaxResponse[],
    travelerType: TravelerTypes,
    uii: string,
    startDate: string = null,
  ): Observable<TravelerPaxResponse[]> {
    return combineLatest([
      this.getTravelersWithFormat$(travelersNotFormat, startDate).pipe(take(1)),
      this.paxDocumentsPipe.transform(travelerType, null).pipe(take(1))
    ]).pipe(
      map(([travelers, documents]) => {
        const _travelersFinalList = [];
        const _travelersFiltered = travelers
          .filter(traveler => {
            return !!travelerType && (traveler.pax_type === PaxType[travelerType] && !traveler.ui) || uii === traveler.ui;
          })
          .sort(compareTravelerPaxResponse)
          .map(traveler => ({
            ...traveler,
            documents: traveler.documents?.filter(
              _doc => documents.some(doc => doc.DOCUMENT_ID.toString() === _doc?.type?.toString())
            )
          }));

        _travelersFiltered.forEach(_traveler => {
          if (_traveler.documents.length > 0) {
            _traveler.documents.forEach(_document => {
              _travelersFinalList.push({
                ..._traveler,
                documentId: _document._id,
                document_number: _document.number,
                configuration_document_id: _document.type ? Number(_document.type) : null,
                documents: [{
                  ..._document,
                }]
              });
            });
          } else {
            _travelersFinalList.push({
              ..._traveler,
              documentId: null,
              document_number: null,
              configuration_document_id: null,
              documents: []
            });
          }
        });
        return _travelersFinalList;
      })
    )
  }

  private getTravelersWithFormat$(travelers: TravelerPaxResponse[], startDate: string): Observable<TravelerPaxResponse[]> {
    const reservationDate = startDate ? new Date(formatStrinWithTimeZoneDateByRegex(startDate)) : null;
    return this.facade.bookingType$.pipe(
      map(bookingType => {
        if (reservationDate && isValidDate(reservationDate)) {
          return travelers.map(traveler => ({
            ...traveler,
            pax_type: getPaxTypeByReservationStartDate(bookingType, traveler, reservationDate)
          }));
        } else return travelers;
      })
    );
  }
}

function compareTravelerPaxResponse(a: TravelerPaxResponse, b: TravelerPaxResponse): number {
  if (a.ui) return -1;
  if (!a.ui) return 1;
  return 0;
}

function getPaxTypeByReservationStartDate(
  booking: TravelBookingType, traveler: TravelerPaxResponse, reservationDate: Date
): PaxType {
  const ageInYears = travelerAgeInYears(traveler.dob, reservationDate);
  const ageInMonths = travelerAgeInMonths(traveler.dob, reservationDate);
  switch (booking) {
    case TravelBookingType.HOTELS:
    case TravelBookingType.PACKAGE_AIR:
      if (ageInYears >= GUEST_CHILDREN_RANGE_AGES.MIN && ageInYears <= GUEST_CHILDREN_RANGE_AGES.MAX) {
        return PaxType.CHILDREN;
      } else return PaxType.ADULTS;
    case TravelBookingType.BUSES:
    case TravelBookingType.FLIGHTS:
    case TravelBookingType.FLIGHTS_BY_SECTOR:
    case TravelBookingType.AUTOS:
      if (ageInMonths >= INFANTS_RANGE_AGES_IN_MONTH.MIN && ageInMonths <= INFANTS_RANGE_AGES_IN_MONTH.MAX) {
        return PaxType.INFANTS;
      } else if (ageInYears >= PASSENGER_CHILDREN_RANGE_AGES.MIN && ageInYears <= PASSENGER_CHILDREN_RANGE_AGES.MAX) {
        return PaxType.CHILDREN;
      } else return PaxType.ADULTS;
    default:
      return traveler.pax_type;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isValidDate(d: any): boolean {
  return Object.prototype.toString.call(d) === '[object Date]' && !isNaN(d) && d instanceof Date;
}
